import { useParams } from "react-router-dom";
import "../../i18n/i18n";
import {
  TextCapitalized,
  Spacer,
  ButtonDS,
  PhoneInputDS,
  hasMobilePhoneFormat,
  triggerToast,
  TextAreaDS,
} from "@qivia/ui";
import { useTranslation } from "react-i18next";
import { driverSlice, selectSendSmsStatus, sendSmsAsync } from "./driversSlice";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import styled from "styled-components";
import { colors } from "@qivia/ui/src/styles/figmaColors";
import { typographies } from "@qivia/ui/src/styles/figmaTypographies";

//DATA VONAGE
const maxLengthMessage = 130;
const maxLength2Messages = 306;

export const RelaunchSmsTab = () => {
  const { t } = useTranslation();
  const params = useParams();
  const dispatch = useAppDispatch();

  const [displayError, setDisplayError] = useState<boolean>(false);
  const [phone, setPhone] = useState<string>("");
  const [phoneHistory, setPhoneHistory] = useState<string>("");
  const [message, setMessage] = useState<string>("");
  const sendSmsStatus = useAppSelector(selectSendSmsStatus);

  const isPhoneFormatValid = useMemo(
    () => (phone?: string) => !!(phone && hasMobilePhoneFormat(phone)),
    [],
  );

  const isMessageLengthMoreThan1Sms = useMemo(
    () => message.length > maxLengthMessage,
    [message],
  );
  const isMessageLengthMoreThan2Sms = useMemo(
    () => message.length > maxLength2Messages,
    [message],
  );

  const phoneError = useMemo(
    () =>
      displayError && phone === ""
        ? t("drivers.error.phone")
        : displayError && !hasMobilePhoneFormat(phone)
          ? t("drivers.error.phone.incorrectFormat")
          : undefined,
    [displayError, phone, t],
  );

  const messageError = useMemo(
    () =>
      displayError && message === ""
        ? t("drivers.error.message")
        : displayError &&
            isMessageLengthMoreThan1Sms &&
            !isMessageLengthMoreThan2Sms
          ? t("drivers.error.message.length.1Sms")
          : displayError && isMessageLengthMoreThan2Sms
            ? t("drivers.error.message.length.2Sms")
            : undefined,
    [
      displayError,
      isMessageLengthMoreThan1Sms,
      isMessageLengthMoreThan2Sms,
      message,
      t,
    ],
  );

  const isValidSubmission = useMemo(
    () =>
      phone !== "" &&
      hasMobilePhoneFormat(phone) &&
      message !== "" &&
      message.length <= maxLength2Messages,
    [phone, message],
  );

  const clickToSeeSmsHistory = useCallback((phoneHistory?: string) => {
    const vonageApiKey = import.meta.env["VITE_VONAGE_API_KEY"] as string;
    if (!vonageApiKey) {
      return;
    }
    const phoneNumber = phoneHistory?.slice(1);
    if (phoneNumber) {
      window.open(
        `https://dashboard.nexmo.com/sms/logs?apiKey=${vonageApiKey}&product=sms&mode=phoneNumber&timeRange_type=FIXED&timeRange_range=LAST_THIRTY_DAYS&direction=outbound&from=&to=${phoneNumber}&messageIds=&recipientNumber=&includeMessage=true`,
        "_blank",
      );
    } else {
      window.open(
        `https://dashboard.nexmo.com/sms/logs?apiKey=${vonageApiKey}&product=sms&mode=timeRange&timeRange_type=FIXED&timeRange_range=LAST_THIRTY_DAYS&direction=outbound&from=&to=&messageIds=&recipientNumber=&includeMessage=true`,
        "_blank",
      );
    }
  }, []);

  const clickToSubmit = () => {
    if (!isValidSubmission) {
      setDisplayError(true);
    } else {
      setDisplayError(false);
      void dispatch(sendSmsAsync({ phone, message }));
      resetValues();
    }
  };

  const resetValues = () => {
    setPhone("");
    setMessage("");
  };

  useEffect(() => {
    if (sendSmsStatus === "success") {
      triggerToast(t("drivers.sendSms.success") || "", "valid");
    } else if (sendSmsStatus === "failed") {
      triggerToast(t("drivers.sendSms.failure") || "", "error");
    }
    void dispatch(driverSlice.actions.resetSendSmsStatus());
  }, [dispatch, sendSmsStatus, t]);

  if (params.tab !== "resendSms") {
    return;
  }

  return (
    <StyledContainer>
      <LeftElement>
        <Spacer y={2} />
        <PhoneInputDS
          value={phone}
          $update={(e) => setPhone(e)}
          $isError={phoneError}
          $width={10}
        />
        {phoneError && (
          <StyledErrorMessage>
            <Spacer y={1} />
            <TextCapitalized>{phoneError}</TextCapitalized>
          </StyledErrorMessage>
        )}
        <Spacer y={2} />
        <StyledTitle>
          <TextCapitalized>{t("drivers.input.message")}</TextCapitalized>
        </StyledTitle>
        <Spacer y={1} />
        <TextAreaDS
          value={message}
          placeholder={t("drivers.input.message.placeholder")}
          $update={(e) => setMessage(e)}
          error={messageError}
        />
        <Spacer y={2} />
        <ButtonDS
          format="hug"
          buttonType="primary"
          text={t("drivers.button.sendSms")}
          onClick={clickToSubmit}
          disabled={!isValidSubmission}
        />
      </LeftElement>
      <Spacer x={8} />
      <RightElement>
        <Spacer y={2} />
        <StyledTitle>
          <TextCapitalized>{t("drivers.seeSmsHistory.title")}</TextCapitalized>
        </StyledTitle>
        <Spacer y={2} />
        <ButtonDS
          format="hug"
          buttonType="secondary"
          text={t("drivers.seeSmsHistory.button")}
          onClick={() => clickToSeeSmsHistory()}
        />
        <Spacer y={2} />
        <StyledTitle>
          <TextCapitalized>
            {t("drivers.seeSmsHistory.forAPhoneNumber.title")}
          </TextCapitalized>
        </StyledTitle>
        <Spacer y={1} />
        <PhoneInputDS
          value={phoneHistory}
          $update={(e) => setPhoneHistory(e)}
          $width={10}
        />
        <Spacer y={1.5} />
        <ButtonDS
          format="hug"
          buttonType="secondary"
          text={t("drivers.seeSmsHistory.forAPhoneNumber.button")}
          onClick={() => {
            if (!isPhoneFormatValid(phoneHistory)) {
              return;
            }
            clickToSeeSmsHistory(phoneHistory);
          }}
          disabled={!isPhoneFormatValid(phoneHistory)}
        />
      </RightElement>
    </StyledContainer>
  );
};

const StyledTitle = styled.div`
  ${typographies["Body/L"]}
  color: ${colors["colors/text/black"]}
`;

const StyledErrorMessage = styled.div`
  display: flex;
  flex-direction: column;
  color: ${colors["colors/system/error/error_normal"]};
  ${typographies["Body/XXS"]};
`;

const StyledContainer = styled.div`
  display: flex;
`;

const LeftElement = styled.div`
  width: 40rem;
`;

const RightElement = styled.div`
  display: flex;
  flex-direction: column;
`;
